import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  IconButton,
  Avatar,
  Box,
  Paper,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import StopIcon from "@mui/icons-material/Stop";
import { startShadowingLoop, IShadowingLoopController } from "@/helpers";
import { useSelector } from "react-redux";
import { IApplicationState } from "@/store-redux";

interface ShadowingGuideDialogProps {
  open: boolean;
  onClose: () => void;
}

const DEMO_TEXT = "Hello, this is a shadowing practice. Try to repeat after me!";
const DEMO_WORDS = DEMO_TEXT.split(" ");

const ShadowingGuideDialog: React.FC<ShadowingGuideDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  const [isShadowingPlaying, setIsShadowingPlaying] = useState(false);
  const [activeWordIdx, setActiveWordIdx] = useState<number | null>(null);
  const [currentLoop, setCurrentLoop] = useState<number>(1);
  const controllerRef = useRef<IShadowingLoopController | null>(null);

  const stopShadowing = () => {
    if (controllerRef.current) {
      controllerRef.current.stop();
      controllerRef.current = null;
    }
    setIsShadowingPlaying(false);
    setActiveWordIdx(null);
  };

  useEffect(() => {
    if (!open) {
      stopShadowing();
    }
    return () => {
      stopShadowing();
    };
  }, [open]);

  const handleToggleShadowing = () => {
    if (isShadowingPlaying) {
      stopShadowing();
    } else {
      setIsShadowingPlaying(true);
      setCurrentLoop(1);
      setActiveWordIdx(0);

      controllerRef.current = startShadowingLoop({
        text: DEMO_TEXT,
        speed: 1.0,
        pauseMs: 1000,
        onWordChange: (idx) => {
          setActiveWordIdx(idx >= 0 ? idx : null);
        },
        onLoopChange: (loop) => {
          setCurrentLoop(loop);
        },
        onStop: () => {
          setIsShadowingPlaying(false);
          setActiveWordIdx(null);
        },
      });
    }
  };

  const handleCloseDialog = () => {
    stopShadowing();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 9999,
      }}
      PaperProps={{
        sx: {
          borderRadius: "32px",
          background: isDark
            ? "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)"
            : "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
          color: isDark ? "white" : "text.primary",
          overflow: "hidden",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <DialogTitle sx={{ p: 4, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Avatar
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                width: 50,
                height: 50,
                boxShadow: "0 8px 16px rgba(102, 126, 234, 0.3)",
              }}
            >
              <SchoolIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  fontSize: "24px",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Phương pháp Shadowing
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDark ? alpha("#fff", 0.6) : "text.secondary", fontWeight: 500 }}
              >
                Bôi đen văn bản → Nghe đọc lặp liên tục & Nhại theo ngay lập tức
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
              "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 4, pt: 2 }}>
        {/* Steps */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3.5}>
          <GuideStep icon={<TouchAppIcon fontSize="small" />} label="Bước 1: Bôi đen đoạn văn bản" bgcolor="#3B82F6" />
          <GuideStep icon={<VolumeUpIcon fontSize="small" />} label="Bước 2: Nghe lặp & Nhại theo bản gốc" bgcolor="#10B981" />
        </Stack>

        {/* Tips Section */}
        <Paper
          elevation={0}
          sx={{
            background: isDark ? alpha("#fff", 0.03) : "white",
            borderRadius: "20px",
            p: 3,
            mb: 3.5,
            border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PsychologyIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: "16px" }}>
              Mẹo để đạt hiệu quả cao
            </Typography>
          </Stack>
          <Stack spacing={1.2}>
            <TipItem text="Bắt đầu với các cụm từ ngắn (2-4 từ) để nắm bắt giai điệu chuẩn" isDark={isDark} />
            <TipItem text="Bắt chước chuẩn xác ngữ điệu, trọng âm và nối âm của người bản xứ" isDark={isDark} />
            <TipItem text="Luyện tập lặp lại nhiều lần cho đến khi phát âm mượt mà, trôi chảy" isDark={isDark} />
            <TipItem text="Nhấp nút Đóng Shadowing để kết thúc lượt luyện tập" isDark={isDark} />
          </Stack>
        </Paper>

        {/* Interactive Demo Player */}
        <Paper
          elevation={0}
          sx={{
            background: isDark
              ? "linear-gradient(135deg, rgba(39, 174, 96, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)"
              : "linear-gradient(135deg, rgba(39, 174, 96, 0.06) 0%, rgba(102, 126, 234, 0.06) 100%)",
            borderRadius: "22px",
            p: 3,
            border: `1px solid ${isDark ? alpha("#27ae60", 0.25) : alpha("#27ae60", 0.3)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <AutoAwesomeIcon sx={{ fontSize: 20, color: "#27ae60" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: "16px", color: isDark ? "#4ade80" : "#1e7e34" }}>
                Trải nghiệm Shadowing mẫu
              </Typography>
            </Stack>

            {isShadowingPlaying && (
              <Chip
                label={`Đang lặp vòng ${currentLoop}`}
                size="small"
                sx={{
                  bgcolor: "#10B981",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "11px",
                  animation: "pulse 1.5s infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0.65 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            )}
          </Box>

          <Paper
            elevation={0}
            sx={{
              background: isDark ? "rgba(255,255,255,0.05)" : "white",
              p: 2.5,
              borderRadius: "16px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, justifyContent: "center" }}>
              {DEMO_WORDS.map((word, wIdx) => {
                const isActive = isShadowingPlaying && activeWordIdx === wIdx;
                return (
                  <Box
                    key={wIdx}
                    sx={{
                      px: 1,
                      py: 0.4,
                      borderRadius: "8px",
                      fontSize: "17px",
                      fontWeight: isActive ? 800 : 600,
                      color: isActive ? "#fff" : isDark ? "#fff" : "#1e293b",
                      bgcolor: isActive ? "#10B981" : "transparent",
                      boxShadow: isActive ? "0 4px 12px rgba(16, 185, 129, 0.4)" : "none",
                      transform: isActive ? "scale(1.08)" : "none",
                      transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {word}
                  </Box>
                );
              })}
            </Box>
          </Paper>

          {/* Action Shadowing Button */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="medium"
              variant={isShadowingPlaying ? "contained" : "outlined"}
              onClick={handleToggleShadowing}
              startIcon={
                isShadowingPlaying ? (
                  <StopIcon sx={{ fontSize: "18px !important" }} />
                ) : (
                  <RecordVoiceOverIcon sx={{ fontSize: "18px !important" }} />
                )
              }
              sx={{
                borderRadius: "100px",
                textTransform: "none",
                fontWeight: 800,
                fontSize: "14px",
                py: 1,
                px: 3,
                bgcolor: isShadowingPlaying ? "#10B981" : alpha("#10B981", 0.08),
                color: isShadowingPlaying ? "#fff" : "#10B981",
                borderColor: alpha("#10B981", 0.4),
                boxShadow: isShadowingPlaying ? "0 4px 16px rgba(16, 185, 129, 0.35)" : "none",
                "&:hover": {
                  bgcolor: isShadowingPlaying ? "#059669" : alpha("#10B981", 0.18),
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {isShadowingPlaying ? "Đóng Shadowing" : "🗣️ Luyện Shadowing thử mẫu"}
            </Button>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 4, pt: 0 }}>
        <Button
          onClick={handleCloseDialog}
          fullWidth
          variant="contained"
          sx={{
            borderRadius: "100px",
            py: 1.3,
            textTransform: "none",
            fontWeight: 800,
            fontSize: "15px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 10px 20px rgba(102, 126, 234, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              transform: "translateY(-2px)",
            },
          }}
        >
          Đã hiểu!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GuideStep = ({ icon, label, bgcolor }: { icon: React.ReactNode; label: string; bgcolor: string }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 2.5,
      py: 1.2,
      borderRadius: "100px",
      bgcolor: alpha(bgcolor, 0.1),
      border: `1px solid ${alpha(bgcolor, 0.25)}`,
      flex: 1,
    }}
  >
    <Box sx={{ color: bgcolor, display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: bgcolor, fontSize: "13px" }}>
      {label}
    </Typography>
  </Box>
);

const TipItem = ({ text, isDark }: { text: string; isDark: boolean }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box sx={{ mt: 0.6, width: 6, height: 6, borderRadius: "50%", bgcolor: "#667eea", flexShrink: 0 }} />
    <Typography variant="body2" sx={{ color: isDark ? alpha("#fff", 0.7) : "text.secondary", fontWeight: 500, fontSize: "13px" }}>
      {text}
    </Typography>
  </Stack>
);

export default ShadowingGuideDialog;
